import React from 'react';
import PropTypes from 'prop-types';

import Chip from 'components/Chip';
import { Icon } from 'utils/icons';
import { formatCurrency } from 'utils/helper';
import { summarizeBudgetActuals } from 'utils/budgetActuals';
import FringeTotalPopover from './FringeTotalPopover';

const BudgetTotalChip = ({
  budgetData,
  currency,
  sessionDelta,
  onResetSession,
}) => {
  const hasSessionDelta =
    Number.isFinite(sessionDelta) && Math.abs(sessionDelta) > 0;
  const isIncrease = sessionDelta > 0;
  const hasFilmIncentives = Boolean(
    Number(budgetData?.film_incentive_count || 0) > 0 ||
      budgetData?.film_incentive_estimate_incomplete
  );
  const hasFringeTotal = Number(budgetData?.fringe_total || 0) > 0;
  const actualSummary = React.useMemo(() => {
    if (budgetData?.has_actuals !== undefined) {
      return {
        hasActuals: Boolean(budgetData.has_actuals),
        actualTotal: Number(budgetData.actual_total || 0),
      };
    }
    return summarizeBudgetActuals(budgetData);
  }, [budgetData]);
  const exchangeRateSummary = budgetData?.exchange_rate_summary || {};
  const hasExchangeRateEstimate = Boolean(
    budgetData?.uses_exchange_rates ||
      exchangeRateSummary.uses_exchange_rates ||
      exchangeRateSummary.has_missing_exchange_rates
  );
  const exchangeRateTitle = exchangeRateSummary.has_missing_exchange_rates
    ? 'This total is approximate because some detail line items use currencies without a current exchange rate.'
    : `This total is approximate because some detail line items are calculated using exchange rates that can fluctuate without warning.${
        exchangeRateSummary.rate_date
          ? ` Rates as of ${exchangeRateSummary.rate_date}.`
          : ''
      }`;

  return (
    <div className="budget-total-wrapper">
      <FringeTotalPopover currency={currency}>
        <Chip
          fringe={
            <div className="budget-total-content">
              {(hasFilmIncentives || actualSummary.hasActuals) && (
                <div
                  className="budget-total-section budget-total-section--adjustments"
                  role="group"
                  aria-label="Credits and adjusted totals"
                >
                  {hasFilmIncentives && (
                    <>
                      <div className="budget-total-metric budget-credit-total">
                        <span className="budget-total-metric-label">
                          Estimated Credits
                        </span>
                        <span className="budget-total-metric-value">
                          {budgetData?.film_incentive_estimate_incomplete
                            ? 'Incomplete'
                            : `−${formatCurrency(
                                budgetData?.film_incentive_total || 0,
                                currency
                              )}`}
                        </span>
                      </div>
                      <div className="budget-total-metric budget-net-production-cost">
                        <span className="budget-total-metric-label">
                          Net Production Cost
                        </span>
                        <span className="budget-total-metric-value">
                          {budgetData?.net_total_cost === null ||
                          budgetData?.net_total_cost === undefined
                            ? 'Incomplete'
                            : formatCurrency(
                                budgetData.net_total_cost,
                                currency
                              )}
                        </span>
                      </div>
                    </>
                  )}
                  {actualSummary.hasActuals && (
                    <div className="budget-total-metric budget-actual-total">
                      <span className="budget-total-metric-label">
                        Actual Total
                      </span>
                      <span className="budget-total-metric-value">
                        {formatCurrency(
                          actualSummary.actualTotal || 0,
                          currency
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {hasFringeTotal && (
                <div
                  className="budget-total-section budget-total-section--fringe"
                  role="group"
                  aria-label="Fringe total"
                >
                  <div className="budget-total-metric fringe-total">
                    <span className="budget-total-metric-label">
                      Fringe Total
                    </span>
                    <span className="budget-total-metric-value">
                      {formatCurrency(budgetData?.fringe_total, currency)}
                    </span>
                  </div>
                </div>
              )}
              <div
                className="budget-total-section budget-total-section--primary"
                role="group"
                aria-label="Project total and session change"
              >
                <div className="budget-total-metric project-total">
                  <span className="budget-total-metric-label">
                    Project Total
                  </span>
                  <span className="budget-total-metric-value">
                    {formatCurrency(budgetData?.total_cost || 0, currency)}
                    {hasExchangeRateEstimate && (
                      <span
                        className="exchange-rate-indicator"
                        title={exchangeRateTitle}
                        aria-label={exchangeRateTitle}
                      >
                        <Icon name="InfoCircleIcon" />
                      </span>
                    )}
                  </span>
                </div>
                {hasSessionDelta ? (
                  <div
                    className="budget-session-delta"
                    title="Change in project total during this session"
                  >
                    <Icon
                      name={isIncrease ? 'CaretUpIcon' : 'CaretDownIcon'}
                      className={
                        isIncrease
                          ? 'budget-session-delta__icon--up'
                          : 'budget-session-delta__icon--down'
                      }
                    />
                    <span className="budget-session-delta__amount">
                      {formatCurrency(Math.abs(sessionDelta), currency)} this
                      session
                      <Icon
                        name="RotateRightIcon"
                        className="budget-session-delta__refresh-icon"
                        onClick={e => {
                          e.stopPropagation();
                          onResetSession?.();
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Reset session - set current total as baseline"
                      />
                    </span>
                  </div>
                ) : (
                  <div className="budget-session-delta budget-session-delta--none">
                    <Icon name="CirclePlayIcon" />
                    <span className="budget-session-delta__amount">
                      No change this session
                    </span>
                  </div>
                )}
              </div>
            </div>
          }
          className="budget-total"
        />
      </FringeTotalPopover>
    </div>
  );
};

BudgetTotalChip.propTypes = {
  budgetData: PropTypes.shape({
    total_cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fringe_total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    film_incentive_total: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    film_incentive_count: PropTypes.number,
    film_incentive_estimate_incomplete: PropTypes.bool,
    net_total_cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    actual_total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    has_actuals: PropTypes.bool,
    uses_exchange_rates: PropTypes.bool,
    exchange_rate_summary: PropTypes.shape({
      uses_exchange_rates: PropTypes.bool,
      has_missing_exchange_rates: PropTypes.bool,
      rate_date: PropTypes.string,
    }),
  }),
  currency: PropTypes.string,
  sessionDelta: PropTypes.number,
  onResetSession: PropTypes.func,
};

BudgetTotalChip.defaultProps = {
  budgetData: {},
  currency: 'USD',
  sessionDelta: 0,
  onResetSession: null,
};

export default BudgetTotalChip;
